import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, MapPin, MessageCircle, Phone, ArrowRight, TrendingUp, CalendarCheck, Inbox } from "lucide-react";
import { getDashboard } from "../lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function PriestDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { getDashboard().then(setD).catch(() => {}); }, []);
  if (!d) return <p className="text-sm text-[#7A6E64]">Loading dashboard…</p>;

  const stats = [
    { label: "New requests", value: d.new_requests, icon: Inbox, tint: "#B26A00", bg: "#FBEAD0" },
    { label: "Scheduled", value: d.scheduled_count, icon: CalendarCheck, tint: "#2E7D46", bg: "#E9F5EC" },
    { label: "Settled", value: inr(d.settled), icon: TrendingUp, tint: "#8B1A2B", bg: "#F4E4E6" },
  ];

  return (
    <div data-testid="priest-dashboard" className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#C6603A]">Priest operations</p>
        <h1 className="mt-1 font-heading text-3xl font-bold sm:text-4xl">Good day, {d.greeting_name}</h1>
        <p className="mt-1 text-sm text-[#7A6E64]">Your schedule, earnings, and customer requests at a glance.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* left: work queue */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between">
            <div><h2 className="font-heading text-xl font-bold">Work queue</h2><p className="text-xs text-[#7A6E64]">Requests and ceremonies that need your attention</p></div>
            <span className="grid h-7 w-7 place-items-center rounded-full bg-[#8B1A2B] text-xs font-bold text-white">{d.queue_scheduled.length + d.queue_requests.length}</span>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 pc-stagger">
            {d.queue_scheduled.map((b) => (
              <div key={b.id} data-testid={`priest-sched-${b.id}`} className="pc-card-hover rounded-2xl border border-[#EEE3D6] bg-white p-4">
                <div className="flex items-start justify-between">
                  <div><h3 className="font-heading text-base font-semibold">{b.ceremony}</h3>
                    <p className="text-sm text-[#7A6E64]">{b.customer}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-[#8A8072]"><MapPin size={12} /> {b.location}</p>
                    <p className="mt-1 font-heading font-bold text-[#8B1A2B]">{inr(b.amount)}</p></div>
                  <span className="rounded-full bg-[#E9F5EC] px-2 py-0.5 text-[10px] font-bold text-[#2E7D46]">SCHEDULED</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button className="grid h-9 w-9 place-items-center rounded-lg border border-[#E7DECF] text-[#8B1A2B] transition hover:bg-[#F4E4E6]"><MessageCircle size={15} /></button>
                  <button className="grid h-9 w-9 place-items-center rounded-lg border border-[#E7DECF] text-[#8B1A2B] transition hover:bg-[#F4E4E6]"><Phone size={15} /></button>
                  <button className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#8B1A2B] py-2.5 text-sm font-semibold text-white transition active:scale-[0.98] hover:bg-[#6F1420]">Trip tools <ArrowRight size={15} /></button>
                </div>
              </div>
            ))}
            {d.queue_requests.map((r) => (
              <div key={r.id} data-testid={`priest-req-${r.id}`} className="pc-card-hover rounded-2xl border border-[#E7C9CD] bg-white p-4">
                <div className="flex items-start justify-between">
                  <div><h3 className="font-heading text-base font-semibold">{r.pooja_name}</h3>
                    <p className="text-sm text-[#7A6E64]">{r.customer_name}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-[#8A8072]"><MapPin size={12} /> {r.address}</p>
                    {(r.budget_min || r.budget_max) && <p className="mt-1 font-heading font-bold text-[#8B1A2B]">{inr(r.budget_min)} – {inr(r.budget_max)}</p>}</div>
                  <span className="rounded-full bg-[#FBEAD0] px-2 py-0.5 text-[10px] font-bold text-[#B26A00]">NEW REQUEST</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Link to="/priest/requests" className="flex-1 rounded-lg bg-[#8B1A2B] py-2.5 text-center text-sm font-semibold text-white transition active:scale-[0.98] hover:bg-[#6F1420]">Send proposal</Link>
                </div>
              </div>
            ))}
            {d.queue_scheduled.length + d.queue_requests.length === 0 && (
              <div className="rounded-2xl border border-dashed border-[#E7DECF] bg-white p-8 text-center text-sm text-[#7A6E64] sm:col-span-2">No open items — you're all caught up.</div>
            )}
          </div>
        </div>

        {/* right: earnings + stats */}
        <div className="space-y-4 lg:sticky lg:top-28 lg:self-start">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#8B1A2B] to-[#5C0F1A] p-5 text-white shadow-[0_20px_40px_-20px_rgba(139,26,43,0.7)]">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-white/70">Scheduled value</p>
                <p className="mt-1 font-heading text-4xl font-bold" data-testid="priest-scheduled-value">{inr(d.scheduled_value)}</p>
                <p className="mt-1 text-xs text-white/75">{d.confirmed_count} confirmed ceremonies in your pipeline</p>
              </div>
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-white/15 text-white"><Wallet size={20} /></span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 lg:grid-cols-1">
            {stats.map((s) => (
              <div key={s.label} className="flex items-center gap-3 rounded-2xl border border-[#EEE3D6] bg-white p-4">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl" style={{ background: s.bg, color: s.tint }}><s.icon size={18} /></span>
                <div>
                  <p className="font-heading text-xl font-bold text-[#2B1B17]">{s.value}</p>
                  <p className="text-[11px] text-[#8A8072]">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/priest/requests" className="flex items-center justify-center gap-1.5 rounded-2xl border border-[#8B1A2B]/20 bg-white py-3 text-sm font-semibold text-[#8B1A2B] transition hover:bg-[#F4E4E6]">
            Browse marketplace <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
}
